import React, { useEffect, useState } from "react"
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import {
  MdPerson,
  MdLogout,
  MdLock,
  MdNotificationsActive,
  MdAttachMoney,
  MdContactSupport,
  MdDescription,
  MdLanguage,
  MdHome,
  MdAdminPanelSettings,
} from "react-icons/md"
import {
  MdMovie,
  MdSchedule,
  MdPhotoCamera,
} from "react-icons/md"
import Dropdown from "../Common/DropDown"
import { updateUser } from "../../store/authSlice"
import { fetchCurrentUser } from "../../services/authService"
import { useDispatch, useSelector } from "react-redux"
import logo from "../../assets/morphosys-logo.png"
import ProfileModals from "../Modals/ProfileModals"
import CreateScriptMenu from "./CreateScriptMenu"
import {
  setConnectionErrorOpen,
  setCreateSciptOpen,
  setIsTranslateOpen,
} from "../../store/navSlice"
import ErrorPopUp from "../Common/ErrorPopUp"
import { logOutAsync } from "../../store/asyncLogout"
import { Modal } from "antd"
import GoogleTranslate from "../../utils/Translate"
import { useAddToHomescreenPrompt } from "../../hooks/useAddToHomescreenPrompt"
import { isAdmin, isStaff } from "../../utils/access"

const NavBar = () => {
  const user = useSelector((state) => state.auth.user)
  const location = useLocation()
  const dispatch = useDispatch()
  const [currentPath, setCurrentPath] = useState(location.pathname)
  const [showModal, setShowModal] = useState("")
  const isCreateOpen = useSelector((state) => state.nav.isCreateScriptOpen)
  const dashboardMode = useSelector((state) => state.nav.dashboardMode)
  const [showBottomNav, setShowBottomNav] = useState(true)
  const [promptToInstall, isIOS, isInstallable] = useAddToHomescreenPrompt()
  const [comingSoon, setComingSoon] = useState("")
  useEffect(() => {
    if (!comingSoon) return
    const t = setTimeout(() => setComingSoon(""), 2000)
    return () => clearTimeout(t)
  }, [comingSoon])

  const setIsCreateOpen = (val) => {
    dispatch(setCreateSciptOpen(val))
  }

  const navigate = useNavigate()

  const [viewportHeight, setViewportHeight] = useState("100svh")

  useEffect(() => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)

    if (!isIOS || !window.visualViewport) return

    let activeViewport = window.visualViewport

    const updateHeight = () => {
      const currentViewport = window.visualViewport || activeViewport
      setViewportHeight(`${currentViewport.height}px`)
    }

    // 1. core binding function
    const setupViewportListener = () => {
      // clean up previous reference just in case
      activeViewport.removeEventListener("resize", updateHeight)

      // refresh the reference to the global object
      activeViewport = window.visualViewport
      activeViewport.addEventListener("resize", updateHeight)
      updateHeight()
    }

    // initial setup
    setupViewportListener()
    window.addEventListener("resize", updateHeight)

    // recovery trigger a: heal when the tab becomes active/visible again
    const handleRecovery = () => {
      setupViewportListener()
    }
    window.addEventListener("focus", handleRecovery)
    document.addEventListener("visibilitychange", handleRecovery)

    // recovery trigger b: periodic heartbeat (e.g., every 30 seconds)
    const heartbeatInterval = setInterval(() => {
      setupViewportListener()
    }, 10000)

    return () => {
      activeViewport.removeEventListener("resize", updateHeight)
      window.removeEventListener("resize", updateHeight)
      window.removeEventListener("focus", handleRecovery)
      document.removeEventListener("visibilitychange", handleRecovery)
      clearInterval(heartbeatInterval)
    }
  }, [])

  useEffect(() => {
    setShowBottomNav(
      location.pathname === "/home" ||
        location.pathname.endsWith("/scripts") ||
        /^\/scripts\/(?!create\/)[^/]+\/?$/.test(location.pathname) ||
        location.pathname.includes("/myprofile") ||
        location.pathname.startsWith("/editor/")
    )
  }, [location])
  // In the editor, step the bottom bar aside while typing (editor focused or keyboard up).
  const isEditorFocus = useSelector((state) => state.script.isEditorFocus)
  const keyboardOpen = typeof viewportHeight === "string" && viewportHeight.endsWith("px") && window.innerHeight - parseFloat(viewportHeight) > 150
  const hideForTyping = location.pathname.startsWith("/editor/") && (isEditorFocus || keyboardOpen)

  const getUserData = async () => {
    try {
      const fresh = await fetchCurrentUser()
      if (fresh) dispatch(updateUser(fresh))
    } catch (err) {
      if (err.message === "Failed to fetch" || err.name === "TypeError") {
        dispatch(setConnectionErrorOpen(true))
      }
      console.error(err)
    }
  }

  const handleLogoClick = () => {
    if (window.location.pathname === "/home") {
      navigate(0)
    } else {
      navigate("/home")
    }
  }

  useEffect(() => {
    setCurrentPath(window.location.pathname)
  }, [window.location.pathname])

  // Always load the latest profile (incl. a fresh photo link) from the server,
  // so a photo uploaded on another device shows here too.
  useEffect(() => {
    getUserData()
  }, [])

  const BottomNavItem = ({ Icon, title, path, state, active }) => {
    const isActive = active ?? path === currentPath
    return (
      <Link
        to={path}
        state={state}
        className={`flex items-center flex-col`}
      >
        <Icon size={25} color={isActive ? "#0995F2" : "#C4C4C4"} />
        <label
          style={{ color: isActive ? "#0995F2" : "#969696" }}
          className={` text-xs`}
        >
          {title}
        </label>
      </Link>
    )
  }

  const BottomNav = () => {
    // Mobile bar: Scripts | Production | Schedule | Shot List | Dashboard
    const onHome = currentPath === "/home"
    const scriptMenuActive = /^\/scripts\/(?!create\/)[^/]+\/?$/.test(currentPath)
    const productionModeActive = onHome && (dashboardMode === "production" || dashboardMode === "schedule")
    const scheduleActive = productionModeActive && dashboardMode === "schedule"
    const productionActive = productionModeActive && !scheduleActive
    const dashboardSection = productionModeActive ? undefined : location.state?.section
    // Shot List remains unavailable; Schedule opens the Shooting Schedule.
    const comingSoonItems = [
      { title: "Shot List", Icon: MdPhotoCamera },
    ]
    return (
      <>
        {comingSoon && (
          <div className="fixed left-0 right-0 bottom-[4.75rem] flex justify-center z-[90] pointer-events-none lg:hidden">
            <div className="bg-[#292929] text-white text-xs font-bold rounded-full px-4 py-2 shadow-lg">
              {comingSoon} is coming soon
            </div>
          </div>
        )}
        <div className="h-16 w-full flex justify-evenly lg:hidden items-center ">
          <button
            onClick={() => {
              const lastId = localStorage.getItem("lastOpenedScriptId")
              if (lastId) {
                navigate(`/editor/${lastId}`)
              } else {
                navigate("/home", { state: { mode: "screenwriting", section: "scripts" } })
              }
            }}
            aria-label="Script"
            className="flex items-center flex-col"
          >
            <MdDescription
              size={25}
              color={
                currentPath.startsWith("/editor/") || scriptMenuActive ||
                (onHome && dashboardSection === "scripts")
                  ? "#0995F2"
                  : "#C4C4C4"
              }
            />
            <label
              style={{
                color:
                  currentPath.startsWith("/editor/") || scriptMenuActive ||
                  (onHome && dashboardSection === "scripts")
                    ? "#0995F2"
                    : "#969696",
              }}
              className="text-xs cursor-pointer"
            >
              Script
            </label>
          </button>
          <button
            onClick={() => {
              // Admins: open the most recent Production Overview directly; otherwise the Production selection screen.
              const currentUser = user || JSON.parse(localStorage.getItem("user") || "null")
              if (isAdmin(currentUser) && isStaff(currentUser)) {
                const lastProduction = localStorage.getItem("lastOpenedProductionId")
                navigate("/home", { state: { mode: "production", openProduction: lastProduction || undefined } })
                return
              }
              const lastId = localStorage.getItem("lastOpenedScriptId")
              if (lastId) navigate(`/editor/${lastId}`, { state: { production: true } })
              else navigate("/home", { state: { section: "scripts" } })
            }}
            aria-label="Production"
            className="flex items-center flex-col"
          >
            <MdMovie size={25} color={productionActive ? "#0995F2" : "#C4C4C4"} />
            <label style={{ color: productionActive ? "#0995F2" : "#969696" }} className="text-xs cursor-pointer">Production</label>
          </button>
          <button
            onClick={() => {
              // Admins: open the Shooting Schedule of the current/last production directly; never stop at the Overview.
              const currentUser = user || JSON.parse(localStorage.getItem("user") || "null")
              if (isAdmin(currentUser) && isStaff(currentUser)) {
                const lastProduction = localStorage.getItem("lastOpenedProductionId")
                navigate("/home", { state: { mode: "production", openProduction: lastProduction || undefined, openSection: "schedule" } })
                return
              }
              setComingSoon("Schedule")
            }}
            aria-label="Schedule"
            className="flex items-center flex-col"
          >
            <MdSchedule size={25} color={scheduleActive ? "#0995F2" : "#C4C4C4"} />
            <label style={{ color: scheduleActive ? "#0995F2" : "#969696" }} className="text-xs cursor-pointer">Schedule</label>
          </button>
          {comingSoonItems.map(({ title, Icon }) => (
            <button
              key={title}
              onClick={() => setComingSoon(title)}
              aria-label={`${title} — coming soon`}
              className="flex items-center flex-col"
            >
              <Icon size={25} color="#C4C4C4" />
              <label style={{ color: "#969696" }} className="text-xs">
                {title}
              </label>
            </button>
          ))}
          <BottomNavItem
            Icon={MdHome}
            title={"Dashboard"}
            path={"/home"}
            state={{ mode: "screenwriting" }}
            active={onHome && !productionModeActive && dashboardSection !== "scripts"}
          />
        </div>
        <div className=" w-[50%] mx-auto bg-[#C4C4C4] mb-2 lg:hidden h-[0.3rem] rounded-full" />
      </>
    )
  }

  const TopNav = () => {
    const getUserRole = () => {
      if (user?.role) return user.role
      const localUser = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null
      return localUser?.role || null
    }

    const ProfileMenu = () => (
      <Dropdown
        alignment="right"
        showArrow={true}
        disabled={showModal !== ""}
        trigger={
          <button className=" flex rounded-full size-12 max-h-[6vh] max-w-[6vh]">
            {user?.photo ? (
              <img
                src={user?.photo}
                className=" shadow-[0_3px_8px_rgba(0,0,0,0.24)] bg-white rounded-full size-full object-cover "
              />
            ) : (
              <div className="h-full shadow-[0_3px_8px_rgba(0,0,0,0.24)] font-semibold text-lg w-full flex justify-center items-center text-center bg-[#D41E79] rounded-full text-white">
                {user?.name?.charAt(0)?.toUpperCase() ||
                  JSON.parse(localStorage.getItem("user"))
                    ?.name?.charAt(0)
                    ?.toUpperCase()}
              </div>
            )}
          </button>
        }
      >
        <div className="p-3 flex flex-col text-left">
          <button
            onClick={() => {
              setShowModal("profileinfo")
            }}
            className="text-start text-sm hover:bg-zinc-100 p-2 rounded text-[#727272] font-bold flex items-center justify-start gap-2 w-full"
          >
            <MdPerson className="size-5" />
            Profile Information
          </button>
          {user?.loginType === "EMAIL" && (
            <button
              onClick={() => {
                setShowModal("changepass")
              }}
              className="text-start text-sm hover:bg-zinc-100 p-2 rounded text-[#727272] font-bold flex items-center justify-start gap-2 w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MdLock className="size-5" />
              Password and Security
            </button>
          )}
          <button
            onClick={() => {
              setShowModal("")
            }}
            className="text-start text-sm text-[#727272] hover:bg-zinc-100 p-2 rounded flex font-bold items-center justify-start gap-2 w-full opacity-50 cursor-not-allowed"
          >
            <MdNotificationsActive className="size-5" />
            Notification
          </button>
          {(isInstallable || isIOS) && (
            <button
              onClick={promptToInstall}
              className="text-start text-sm hover:bg-zinc-100 p-2 rounded text-[#727272] font-bold flex items-center justify-start gap-2 w-full"
            >
              <MdHome className="size-5" />
              Add to Home{" "}
              {isIOS ? "(Tap share to install)" : "(Click to install)"}
            </button>
          )}
          <button
            onClick={() => {
              navigate("myprofile/subscription")
            }}
            className="text-[#727272] text-sm text-start hover:bg-zinc-100 p-2 rounded font-bold flex items-center justify-start gap-2 w-full"
          >
            <MdAttachMoney className="size-5" />
            Subscription
          </button>
          <button
            onClick={() =>
              window.open("https://morphosys.io/#signup", "_blank")
            }
            className="text-start text-sm hover:bg-zinc-100 p-2 rounded text-[#727272] font-bold flex items-center justify-start gap-2 w-full"
          >
            <MdContactSupport className="size-5" />
            Support
          </button>
          <button
            onClick={() => {
              dispatch(setIsTranslateOpen(true))
            }}
            className="text-start text-sm text-[#727272] hover:bg-zinc-100 p-2 rounded font-bold flex items-center justify-start gap-2 w-full "
          >
            <MdLanguage className="size-5" />
            Language
          </button>
          {["ADMIN", "SUPPORT"].includes(getUserRole()) && (
            <button
              onClick={() => {
                navigate("/admin")
              }}
              className="text-start text-sm hover:bg-zinc-100 p-2 rounded text-[#727272] font-bold flex items-center justify-start gap-2 w-full"
            >
              <MdAdminPanelSettings className="size-5" />
              Admin
            </button>
          )}
          <button
            className="text-start text-[#727272] text-sm hover:bg-zinc-100 p-2 rounded flex items-center justify-start font-bold gap-2 w-full"
            onClick={() => {
              dispatch(logOutAsync())
              localStorage.removeItem("user")
              localStorage.removeItem("session")
              navigate("/login")
            }}
          >
            <MdLogout className="size-5 " />
            Log Out
          </button>
        </div>
      </Dropdown>
    )

    return (
      <div className="  h-[6.9vh] max-lg:hidden">
        <header className="bg-[#FFA300] w-full max-lg:hidden fixed header top-0 h-[7vh]">
          <div className="w-full h-full relative ">
            <div className="relative w-full h-full overflow-hidden">
              <div className="top-triangle left-[1%] w-[3%] h-[90%] absolute z-[4] rotate-[-25deg] top-[-10.5%]" />
              <div className="h-[999px] w-[999px] left-[50%] top-[-2%] dark-blue-circle-bg rounded-full z-[2] absolute" />
            </div>
            <div className="absolute top-0 flex z-[15] w-full h-full items-center justify-between px-4">
              <button onClick={handleLogoClick}>
                <img className="h-16 max-h-[7vh]" src={logo} />
              </button>

              {/* Profile menu hidden on the Dashboard (/home) — Profile lives in the Dashboard navigation */}
              {location.pathname !== "/home" && <ProfileMenu />}
            </div>
          </div>
        </header>
      </div>
    )
  }

  return (
    <>
      <nav
        className="flex fixed top-0 w-full flex-col h-[100svh] z-[80] lg:flex-col-reverse navwrapper"
        style={{ height: viewportHeight }}
      >
        <div className="grow overflow-y-auto">
          {" "}
          <Outlet />
        </div>
        <div className="bg-white z-[80] max-lg:drop-shadow-[0_10px_8px_rgba(0,0,0,0.2)] border-b">
          {showBottomNav && !hideForTyping && <BottomNav />}
          <TopNav />
          {isCreateOpen && (
            <CreateScriptMenu
              isCreateOpen={isCreateOpen}
              setIsCreateOpen={setIsCreateOpen}
            />
          )}
          <div className="">
            <ProfileModals showModal={showModal} setShowModal={setShowModal} />
          </div>
        </div>
      </nav>
    </>
  )
}

export default NavBar
